import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { retentionReference } from "./retention_reference";
import { locationCustomerDeletionPath, locationCustomerPath } from "@/routes";
import type { CustomerProfile } from "../customers/types";

export default function NewDeletion({
  customer,
}: {
  customer: Pick<CustomerProfile, "id" | "name" | "revision">;
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  const [rule, setRule] = useState("custom");
  const [yearEnd, setYearEnd] = useState("");
  const [filed, setFiled] = useState("");
  const [due, setDue] = useState("");
  const [exception, setException] = useState("");
  const isUS = rule.startsWith("us_");
  const reference =
    !isUS || exception === "none"
      ? retentionReference(rule, yearEnd, filed, due)
      : null;
  if (!auth.location) return null;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: customer.name,
          href: locationCustomerPath(auth.location.id, customer.id),
        },
      ]}
    >
      <Head title={t("customer_deletions.title")} />
      <PageCanvas>
        <h1 className="text-page font-display">
          {t("customer_deletions.title")}
        </h1>
        <Card>
          <CardHeader>
            <CardTitle>{customer.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>{t("customer_deletions.explanation")}</p>
            <p className="text-muted-foreground text-sm">
              {t("customer_deletions.resolve")}
            </p>
            <Form
              action={locationCustomerDeletionPath(
                auth.location.id,
                customer.id,
              )}
              method="post"
            >
              {({ errors, processing }) => (
                <div className="space-y-5">
                  <input
                    type="hidden"
                    name="revision"
                    value={customer.revision}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="deletion-confirmation">
                      {t("customer_deletions.confirm", { name: customer.name })}
                    </Label>
                    <Input
                      id="deletion-confirmation"
                      name="confirmation"
                      required
                      autoComplete="off"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="retention-rule">
                      {t("customer_deletions.rule")}
                    </Label>
                    <NativeSelect
                      id="retention-rule"
                      name="retention_rule"
                      value={rule}
                      onChange={(event) => setRule(event.target.value)}
                    >
                      {[
                        "custom",
                        "ro_accounting",
                        "us_ordinary",
                        "us_omission",
                      ].map((value) => (
                        <NativeSelectOption key={value} value={value}>
                          {t(`customer_deletions.rules.${value}`)}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                    <p className="text-muted-foreground text-sm">
                      {t("customer_deletions.rule_help")}
                    </p>
                  </div>
                  {rule === "ro_accounting" && (
                    <div className="space-y-2">
                      <Label htmlFor="financial-year-end">
                        {t("customer_deletions.year_end")}
                      </Label>
                      <Input
                        id="financial-year-end"
                        name="financial_year_end"
                        type="date"
                        required
                        value={yearEnd}
                        onChange={(event) => setYearEnd(event.target.value)}
                      />
                      <p className="text-muted-foreground text-sm">
                        {t("customer_deletions.ro_help")}{" "}
                        <a
                          className="underline"
                          href="https://legislatie.just.ro/Public/DetaliiDocument/1576"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("customer_deletions.ro_source")}
                        </a>
                      </p>
                    </div>
                  )}
                  {isUS && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="return-filed-on">
                          {t("customer_deletions.filed")}
                        </Label>
                        <Input
                          id="return-filed-on"
                          name="return_filed_on"
                          type="date"
                          required
                          value={filed}
                          onChange={(event) => setFiled(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="original-return-due-on">
                          {t("customer_deletions.return_due")}
                        </Label>
                        <Input
                          id="original-return-due-on"
                          name="original_return_due_on"
                          type="date"
                          required
                          value={due}
                          onChange={(event) => setDue(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="retention-exception">
                          {t("customer_deletions.exception")}
                        </Label>
                        <NativeSelect
                          id="retention-exception"
                          name="retention_exception"
                          required
                          value={exception}
                          onChange={(event) => setException(event.target.value)}
                        >
                          <NativeSelectOption value="">
                            {t("customer_deletions.exceptions.unknown")}
                          </NativeSelectOption>
                          <NativeSelectOption value="none">
                            {t("customer_deletions.exceptions.none")}
                          </NativeSelectOption>
                          <NativeSelectOption value="special">
                            {t("customer_deletions.exceptions.special")}
                          </NativeSelectOption>
                        </NativeSelect>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {t("customer_deletions.us_help")}{" "}
                        <a
                          className="underline"
                          href="https://www.irs.gov/businesses/small-businesses-self-employed/how-long-should-i-keep-records"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("customer_deletions.us_source")}
                        </a>
                        {" · "}
                        <a
                          className="underline"
                          href="https://www.irs.gov/irm/part25/irm_25-006-001r"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("customer_deletions.us_timing_source")}
                        </a>
                      </p>
                    </div>
                  )}
                  {reference && (
                    <p role="status" className="rounded-md border p-3 text-sm">
                      {t(
                        isUS
                          ? "customer_deletions.us_reference"
                          : "customer_deletions.ro_reference",
                        { date: reference },
                      )}
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="retention-basis">
                      {t("customer_deletions.basis")}
                    </Label>
                    <Input
                      id="retention-basis"
                      name="retention_basis"
                      maxLength={rule === "custom" ? 500 : 300}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="retain-until">
                      {t("customer_deletions.deadline")}
                    </Label>
                    <Input id="retain-until" name="retain_until" type="date" />
                    <p className="text-muted-foreground text-sm">
                      {t("customer_deletions.policy")}
                    </p>
                  </div>
                  {Object.values(errors).length > 0 && (
                    <div role="alert" className="text-destructive text-sm">
                      {Object.values(errors).join(". ")}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={processing}
                    >
                      {t("customer_deletions.start")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={locationCustomerPath(
                            auth.location!.id,
                            customer.id,
                          )}
                        />
                      }
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
